import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import MonitoringQueueGroupsService from 'services/ServiceMONITORINGQUEUEGROUPS';
import * as yup from 'yup';

const createPayloadSchema = yup.object().shape({
  name: yup.string().required('O nome do agrupamento é obrigatório'),
  queues: yup
    .array()
    .of(yup.number().integer())
    .min(1, 'Selecione pelo menos uma fila')
    .required('A lista de filas é obrigatória'),
  accessAgentsLogin: yup
    .array()
    .of(yup.string())
    .min(1, 'Selecione pelo menos um usuário')
    .required('Selecione pelo menos um usuário')
});

const groupItemSchema = yup.object().shape({
  id: yup.number().required(),
  name: yup.string().required(),
  queues: yup.array().nullable(),
  agents: yup.array().nullable(),
  accessAgentsLogin: yup.array().nullable(),
  created_at: yup.string().nullable(),
  updated_at: yup.string().nullable(),
  created_by: yup.string().nullable()
});

const groupListSchema = yup.array().of(groupItemSchema);

const apiResponseSchema = yup.object().shape({
  status: yup.string().oneOf(['OK', 'ERROR']).required(),
  message: yup.string().nullable(),
  data: yup.mixed(),
  errors: yup.array().nullable(),
  totalRegisters: yup.number().nullable()
});

const useMonitoringQueueGroups = (props = {}) => {
  const { itemsPerPage = 10 } = props;
  const login = localStorage.getItem('login');

  const [state, setState] = useState({
    response: [],
    isLoading: false,
    isError: false,
    error: null,
    totalRegisters: 0,
    activePage: 1,
    searchText: ''
  });

  const hasInitialFetch = useRef(false);

  const options = useMemo(
    () => state.response.map(({ id, name }) => ({ label: name, value: id })),
    [state.response]
  );

  const fetchData = useCallback(
    async (selectedPage, search) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));

      try {
        const response = await MonitoringQueueGroupsService.list(
          selectedPage,
          itemsPerPage,
          search
        );

        const apiResValidation = apiResponseSchema.safeValidateSync(
          response.data,
          {
            abortEarly: false
          }
        );

        if (!apiResValidation.success) {
          throw {
            name: 'ValidationError',
            message: apiResValidation.error.message
          };
        }

        const apiData = apiResValidation.data;

        if (apiData.status !== 'OK') {
          throw new Error(apiData.message || 'Erro ao listar agrupamentos.');
        }

        const listValidation = groupListSchema.safeValidateSync(
          apiData.data || [],
          {
            abortEarly: false
          }
        );

        if (!listValidation.success) {
          throw {
            name: 'ValidationError',
            message: listValidation.error.message
          };
        }

        const validatedList = listValidation.data;

        const filteredList = validatedList.filter((group) => {
          if (!login) return false;
          if (login === 'adm') return true;

          const createdBy = group.createdBy;
          if (createdBy === login) return true;

          const accessAgents = group.accessAgentsLogin || [];
          if (Array.isArray(accessAgents)) {
            return accessAgents.some((agent) => {
              if (typeof agent === 'object' && agent !== null)
                return agent.login === login;
              return false;
            });
          }

          return false;
        });

        setState((prevState) => ({
          ...prevState,
          response: filteredList,
          totalRegisters: apiData.totalRegisters || filteredList.length,
          isLoading: false,
          activePage: selectedPage,
          searchText: search
        }));
      } catch (err) {
        console.error({ err });
        const errorMessage =
          err.name === 'ValidationError'
            ? 'Dados inválidos recebidos do servidor.'
            : err.response?.data?.message ||
              err.message ||
              'Erro ao carregar agrupamentos.';

        toast.error(errorMessage);

        setState((prevState) => ({
          ...prevState,
          response: [],
          isLoading: false,
          isError: true,
          error: err
        }));
      }
    },
    [itemsPerPage]
  );

  const fetch = useCallback(
    (page, search) => {
      fetchData(page, search);
    },
    [fetchData]
  );

  const handlePageChange = useCallback(
    (page) => {
      fetch(page, state.searchText);
    },
    [fetch, state.searchText]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setState((prevState) => ({ ...prevState, searchText: value }));
      fetch(1, value);
    },
    [fetch]
  );

  const getGroupById = useCallback(async (id) => {
    try {
      const response = await MonitoringQueueGroupsService.findById(id);

      const apiResValidation = apiResponseSchema.safeValidateSync(
        response.data,
        {
          abortEarly: false
        }
      );

      if (!apiResValidation.success) {
        throw {
          name: 'ValidationError',
          message: apiResValidation.error.message
        };
      }

      const apiData = apiResValidation.data;

      if (apiData.status !== 'OK') {
        throw new Error(apiData.message || 'Erro ao buscar detalhes.');
      }

      const itemValidation = groupItemSchema.safeValidateSync(apiData.data, {
        abortEarly: false
      });

      if (!itemValidation.success) {
        throw {
          name: 'ValidationError',
          message: itemValidation.error.message
        };
      }

      return itemValidation.data;
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Erro ao buscar detalhes do agrupamento.';
      toast.error(msg);
      return null;
    }
  }, []);

  const createGroup = useCallback(
    async (data) => {
      try {
        const validation = createPayloadSchema.safeValidateSync(data);

        if (!validation.success) {
          throw { name: 'ValidationError', message: validation.error.message };
        }

        const response = await MonitoringQueueGroupsService.create(data);

        if (response.data && response.data.status === 'ERROR') {
          throw new Error(response.data.message);
        }

        toast.success('Agrupamento criado com sucesso!');
        fetch(state.activePage, state.searchText);
        return true;
      } catch (err) {
        const msg =
          err.name === 'ValidationError'
            ? err.message
            : err.response?.data?.message || err.message || 'Erro ao criar.';
        toast.error(msg);
        return false;
      }
    },
    [fetch, state.activePage, state.searchText]
  );

  const updateGroup = useCallback(
    async (id, data) => {
      try {
        const validation = createPayloadSchema.safeValidateSync(data);

        if (!validation.success) {
          throw { name: 'ValidationError', message: validation.error.message };
        }

        const response = await MonitoringQueueGroupsService.update(id, data);

        if (response.data && response.data.status === 'ERROR') {
          throw new Error(response.data.message);
        }

        toast.success('Agrupamento atualizado com sucesso!');
        fetch(state.activePage, state.searchText);
        return true;
      } catch (err) {
        const msg =
          err.name === 'ValidationError'
            ? err.message
            : err.response?.data?.message ||
              err.message ||
              'Erro ao atualizar.';
        toast.error(msg);
        return false;
      }
    },
    [fetch, state.activePage, state.searchText]
  );

  const deleteGroup = useCallback(
    async (id) => {
      try {
        const response = await MonitoringQueueGroupsService.delete(id);

        if (response.data && response.data.status === 'ERROR') {
          throw new Error(response.data.message);
        }

        toast.success('Agrupamento removido.');

        const newPage =
          state.response.length === 1 && state.activePage > 1
            ? state.activePage - 1
            : state.activePage;

        fetch(newPage, state.searchText);
        return true;
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            'Erro ao remover agrupamento.'
        );
        return false;
      }
    },
    [fetch, state.activePage, state.searchText, state.response.length]
  );

  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchData(1, '');
    }
  }, [fetchData]);

  return {
    ...state,
    options,
    fetch,
    handlePageChange,
    handleSearchChange,
    getGroupById,
    createGroup,
    updateGroup,
    deleteGroup
  };
};

export default useMonitoringQueueGroups;
